import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ChargeFiltersScreen.module.scss';
import Button from '../../../common/components/button/Button';
import FormItem from '../../../common/components/formItem/FormItem';
import Label from '../../../common/components/label/Label';
import { Col, Row } from 'react-flexbox-grid';
import { SelectValueLabel } from '../../../common/types/SelectValueLabel';
import VehiclesService from '../../../api/vehicles/VehiclesService';
import GenericTypesService from '../../../api/genericTypes/GenericTypesService';
import SuppliersService from '../../../api/suppliers/SuppliersService';
import Select from 'common/components/select/Select';
import { Controller, useForm } from 'react-hook-form';
import UsersService from 'api/users/UsersService';
import { UsersSelectItemDto } from 'api/users/models/UserDto';
import { GenericType } from 'api/genericTypes/enums/GenericType';
import Utils from 'common/services/Utils';
import { useParams } from 'react-router-dom';
import RangeInputPicker from 'common/components/rangeInputPicker/RangeInputPicker';
import { RequestStatusType, SituationType } from 'screens/vehicle/components/carChargeTab/components/ChargeScreen';
import { ChargesStatusColorEnum } from 'api/charges/enums/ChargesStatusColorEnum';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import Loading from 'common/services/Loading';
import ToggleSwitch from 'common/components/toggleSwitch/ToggleSwitch';
import { FaInfoCircle } from 'react-icons/fa';
import ReactTooltip from 'react-tooltip';
import moment from 'moment';
import GeofencesService from 'api/geofences/GeofencesService';
import VehicleSegmentsService from 'api/vehicleSegments/VehicleSegmentsService';
import LocalsService from 'api/locals/LocalsService';

export interface Filters {
    startDate?: Date | null;
    endDate?: Date | null;
    vehicleId?: string;
    vehicleName?: string;
    typeId?: string;
    typeName?: string;
    type?: string;
    supplierId?: string;
    supplierName?: string;
    responsibleId?: string;
    responsibleName?: string;
    cost?: number[];
    refundState?: string;
    refundStateName?: string;
    refundSituation?: string;
    vehiclesInactive?: boolean;
    page?: number;
    fuelId?: string;
    fuelName?: string;
    geofenceId?: string;
    geofenceName?: string;
    hasGeofence?: boolean;
    localIds?: string[];
    localNames?: string[];
    localOperator?: string;
    segmentId?: string;
    segmentName?: string;
    orderColumn?: string;
    orderBy?: string;
    allIn?: string;
    scrollToItemId?: string;
}

type Props = {
    isVehicle?: boolean;
    isDriver?: boolean;
    filters: Filters;
    onChange: (filters: Filters) => void;
    onFilter: (filters: Filters) => void;
    hideGeoFenceFilters?: boolean;
}

const ChargeFiltersScreen: React.FC<Props> = ({ isVehicle, isDriver, filters, onChange, onFilter, hideGeoFenceFilters }: Props) => {
    const { t } = useTranslation();

    const { driverId, vehicleId, month } = useParams<{ driverId: string, vehicleId: string, month: string }>();

    const form = useForm<Filters>({ shouldUnregister: false, shouldFocusError: true, defaultValues: filters });
    const { setValue, watch, getValues, control } = form;
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const [vehiclesOptions, setVehicles] = useState<SelectValueLabel[]>([]);
    const [typesOptions, setTypes] = useState<SelectValueLabel[]>([]);
    const [suppliersOptions, setSuppliers] = useState<SelectValueLabel[]>([]);
    const [responsiblesOptions, setResponsibles] = useState<SelectValueLabel[]>([]);
    const [selectedRefundTreated, setSelectedRefundTreated] = useState<boolean | undefined>(undefined);
    const [reRender, setReRender] = useState<number>(0);
    const [fuelsOptions, setFuels] = useState<SelectValueLabel[]>([]);
    const [geofencesOptions, setGeofences] = useState<SelectValueLabel[]>([]);
    const [segmentsOptions, setSegments] = useState<SelectValueLabel[]>([]);
    const [localsOptions, setLocals] = useState<SelectValueLabel[]>([]);
    const hasReadCostCenterPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_COST_CENTER_READ']);
    const [selectedHasGeofence, setSelectedHasGeofence] = useState<boolean | undefined>(undefined);

    const refundStatusOptions: SelectValueLabel[] = [{
        value: RequestStatusType.PENDING,
        label: t('charges.list.request_status_type.PENDING'),
    }, {
        value: RequestStatusType.APPROVED,
        label: t('charges.list.request_status_type.APPROVED'),
    }, {
        value: RequestStatusType.REFUSED,
        label: t('charges.list.request_status_type.REFUSED'),
    }, {
        value: RequestStatusType.NONE,
        label: t('charges.list.request_status_type.NONE'),
    }]

    const getData = async () => {
        try {
            Loading.show();
            const [vehiclesOptions, typesOptions, suppliersOptions, responsiblesOptions, fuels, geofencesOptions, segmentsOptionsDB, localsOptionsDB] = await Promise.all([
                VehiclesService.catalogByCharges({}),
                GenericTypesService.catalogCharge(),
                SuppliersService.catalog({ referencedAtCharges: true }),
                UsersService.getAllForCompany(),
                VehiclesService.getFuelList(),
                hasReadCostCenterPolicy ? GeofencesService.catalog() : [],
                VehicleSegmentsService.catalog(),
                LocalsService.catalog(),
            ]);
            setVehicles(vehiclesOptions);

            const types2 = typesOptions.map(x => ({ ...x, name: x.label, label: x.isSystem ? t(`common.generic_types.${x.label}` as any) : x.label }));
            const types = Utils.sortAlphabetically(types2);
            setTypes(types);

            setSuppliers(suppliersOptions);
            setResponsibles((responsiblesOptions || []).map((x: UsersSelectItemDto) => ({ value: x.id || '', label: x.realName || '' })));

            setFuels(Utils.sortAlphabetically(fuels.map(x => ({ value: x.value, label: (t(('vehicle.fuels.' + x.label) as any)), fullLabel: x.label }))));
            setGeofences(geofencesOptions ?? []);
            setSegments(Utils.sortAlphabetically(segmentsOptionsDB));
            setLocals([{ value: '00000000-0000-0000-0000-000000000000', label: t('maintenances.list.none') as string }, ...Utils.sortAlphabetically(localsOptionsDB)]);

            Loading.hide();
        } catch (error) {
            Loading.hide();
        }
    }

    useEffect(() => {
        if (form.getValues('refundSituation')) {
            setSelectedRefundTreated(form.getValues('refundSituation') == SituationType.TREATED);
        }
        if (form.getValues('hasGeofence') != undefined) {
            setSelectedHasGeofence(form.getValues('hasGeofence'));
        }
        getData();
    }, []);

    useEffect(() => {
        ReactTooltip.rebuild()
    }, []);

    const clearFilters = () => {
        form.reset({
            startDate: undefined,
            endDate: undefined,
            vehicleId: isVehicle && !Utils.isStringNullOrEmpty(vehicleId) ? vehicleId : undefined,
            typeId: undefined,
            supplierId: undefined,
            responsibleId: isDriver && !Utils.isStringNullOrEmpty(driverId) ? driverId : undefined,
            cost: undefined,
            vehicleName: undefined,
            typeName: undefined,
            type: undefined,
            supplierName: undefined,
            responsibleName: undefined,
            refundState: undefined,
            refundStateName: undefined,
            refundSituation: undefined,
            vehiclesInactive: false,
            fuelId: undefined,
            geofenceId: undefined,
            geofenceName: undefined,
            hasGeofence: undefined,
            localIds: [],
            localNames: [],
            localOperator: 'OR',
            segmentId: undefined,
            segmentName: undefined,
            orderBy: 'desc',
            orderColumn: 'c.date'
        });
        onInputChange();
        onSubmit();
    }

    const onSubmit = () => {
        const f = getValues();
        onFilter({ ...f, responsibleId: isDriver && !Utils.isStringNullOrEmpty(driverId) ? driverId : f.responsibleId });
    }

    const onInputChange = () => {
        if (getValues('type') != GenericType.FUEL) {
            setValue('fuelId', undefined);
        }
        onChange(getValues());

        setReRender(reRender + 1);
    }

    const getVal = (arr: any[] | undefined, index: number): any | undefined => {
        return arr && arr.length > index ? arr[index] : undefined;
    }


    const onSelectRefundTreated = (mode: boolean, type?: SituationType) => {
        if (form.getValues('refundState') !== RequestStatusType.APPROVED) {
            return;
        }
        const aux = selectedRefundTreated === mode ? undefined : mode;
        setSelectedRefundTreated(aux);
        setValue('refundSituation', aux === undefined ? undefined : type);
        onInputChange();
    }

    const onSelectHasGeofence = (mode: boolean) => {

        const aux = selectedHasGeofence === mode ? undefined : mode;
        setSelectedHasGeofence(aux);
        setValue('hasGeofence', aux);
        onInputChange();
    }


    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className={styles.formContent}>
                <div>
                    <FormItem>
                        <Row key={'chargesFiltersRow' + reRender}>
                            {!isVehicle && <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.vehicle')}
                                        <FaInfoCircle data-tip={t('charges.only_vehicles_with_expenses')} data-for={'activesTooltip'} style={{ marginLeft: '0.5rem' }} />
                                    </Label>
                                    <ReactTooltip id={'activesTooltip'} place="right" type="dark" effect="solid" />

                                    <Controller
                                        render={({ onChange, value }) => {
                                            return (
                                                <Select
                                                    options={vehiclesOptions}
                                                    isClearable
                                                    placeholder={t('vehicle.vehicle')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        setValue('vehicleName', vehiclesOptions.find(x => x.value == data?.value)?.label ?? null);
                                                        onChange(data?.value ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={vehiclesOptions.find(x => x.value === value) ? { label: vehiclesOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="vehicleId"
                                        defaultValue={getValues('vehicleId')} />
                                </FormItem>
                            </Col>}

                            <Col xs={!isVehicle ? 6 : 12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('common.type')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            return (
                                                <Select
                                                    options={typesOptions}
                                                    isClearable
                                                    placeholder={t('common.type')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        const _type = typesOptions.find(x => x.value == data?.value);
                                                        setValue('type', _type?.fullLabel ?? null);
                                                        setValue('typeName', _type?.label ?? null);
                                                        onChange(data?.value ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={typesOptions.find(x => x.value === value) ? { label: typesOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="typeId"
                                        defaultValue={getValues('typeId')} />
                                </FormItem>
                            </Col>
                            {getValues('type') == GenericType.FUEL && <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.fuel')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            return (
                                                <Select
                                                    options={fuelsOptions}
                                                    isClearable
                                                    placeholder={t('vehicle.fuel')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        setValue('fuelName', fuelsOptions.find(x => x.value == data?.value)?.label ?? null);
                                                        onChange(data?.value ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={fuelsOptions.find(x => x.value === value) ? { label: fuelsOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="fuelId"
                                        defaultValue={getValues('fuelId')} />
                                </FormItem>
                            </Col>}
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('charges.reimbursement_status')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            return (
                                                <Select
                                                    options={refundStatusOptions}
                                                    isClearable
                                                    placeholder={t('common.state')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        setValue('refundStateName', refundStatusOptions.find(x => x.value == data?.value)?.label ?? null);
                                                        onChange(data?.value ?? null);

                                                        if (data?.value !== RequestStatusType.APPROVED) {
                                                            setValue('refundSituation', undefined);
                                                            setSelectedRefundTreated(undefined)
                                                        }
                                                        onInputChange();
                                                    }}
                                                    value={refundStatusOptions.find(x => x.value === value) ? { label: refundStatusOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="refundState"
                                        defaultValue={getValues('refundState')} />
                                </FormItem>
                            </Col>

                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>
                                        {t('charges.reimbursement_situation')}
                                        <FaInfoCircle data-tip={t('charges.situation_with_status_approved_tooltip')} data-for={'situationTooltip'} style={{ marginLeft: '0.5rem' }} />
                                    </Label>
                                    <ReactTooltip id={'situationTooltip'} place="top" type="dark" effect="solid" />
                                    <div className={styles.situationDetails} style={{ cursor: form.watch('refundState') !== RequestStatusType.APPROVED ? 'not-allowed' : 'pointer' }}>
                                        <div onClick={() => onSelectRefundTreated(true, SituationType.TREATED)} style={{ width: '50%' }}>
                                            <div className={styles.labelContainer}>
                                                <div className={selectedRefundTreated ? styles.selected : styles.notSelected} style={{ background: selectedRefundTreated ? (ChargesStatusColorEnum as any)[SituationType.TREATED] : form.watch('refundState') !== RequestStatusType.APPROVED ? '#eee' : null }}>
                                                    <span>{t(('charges.situations.' + SituationType.TREATED) as any)}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div onClick={() => onSelectRefundTreated(false, SituationType.NOT_TREATED)} style={{ width: '50%' }}>
                                            <div className={styles.labelContainer}>
                                                <div className={selectedRefundTreated != undefined && !selectedRefundTreated ? styles.selected : styles.notSelected} style={{ background: selectedRefundTreated != undefined && !selectedRefundTreated ? (ChargesStatusColorEnum as any)[SituationType.NOT_TREATED] : form.watch('refundState') !== RequestStatusType.APPROVED ? '#eee' : null }}>
                                                    <span>{t(('charges.situations.' + SituationType.NOT_TREATED) as any)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </FormItem>
                            </Col>

                            {!isDriver && <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('common.responsible')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            return (
                                                <Select
                                                    options={responsiblesOptions}
                                                    isClearable
                                                    placeholder={t('common.responsible')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        setValue('responsibleName', responsiblesOptions.find(x => x.value == data?.value)?.label ?? null);
                                                        onChange(data?.value ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={responsiblesOptions.find(x => x.value === value) ? { label: responsiblesOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="responsibleId"
                                        defaultValue={getValues('responsibleId')} />
                                </FormItem>
                            </Col>}
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('common.supplier')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            return (
                                                <Select
                                                    options={suppliersOptions}
                                                    isClearable
                                                    placeholder={t('common.supplier')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        setValue('supplierName', suppliersOptions.find(x => x.value == data?.value)?.label ?? null);
                                                        onChange(data?.value ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={suppliersOptions.find(x => x.value === value) ? { label: suppliersOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="supplierId"
                                        defaultValue={getValues('supplierId')} />
                                </FormItem>
                            </Col>
                            <Col xs={12}>
                                <RangeInputPicker
                                    label={t('common.cost')}
                                    onChange={(startValue: any, endValue: any) => {
                                        const range = [];
                                        range.push(startValue != null && startValue != undefined ? Number(startValue) : undefined);
                                        range.push(endValue != null && endValue != undefined ? Number(endValue) : undefined);
                                        setValue('cost', range);
                                    }}
                                    defaultStartValue={getVal(getValues('cost'), 0)}
                                    defaultEndValue={getVal(getValues('cost'), 1)}
                                    groupSymbol={t('common.euro')}
                                />
                            </Col>
                            <Col xs={12}>
                                <RangeInputPicker
                                    label={t('charges.filter_date')}
                                    onChange={(startValue: any, endValue: any) => {
                                        // Limites do dia no fuso do utilizador: o backend usa-os tal como vêm.
                                        setValue('startDate', startValue ? moment(startValue).startOf('day').toDate() : undefined);
                                        setValue('endDate', endValue ? moment(endValue).endOf('day').toDate() : undefined);
                                        onInputChange();

                                    }}
                                    defaultStartValue={getValues('startDate')}
                                    defaultEndValue={getValues('endDate')}
                                    isDatePicker
                                />
                            </Col>

                            {(hasReadCostCenterPolicy && !hideGeoFenceFilters) && <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('charges.geofences.title')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            return (
                                                <Select
                                                    options={geofencesOptions}
                                                    isClearable
                                                    placeholder={t('charges.geofences.title')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        if (data?.value) {
                                                            setSelectedHasGeofence(undefined);
                                                            setValue('hasGeofence', undefined);
                                                        }
                                                        setValue('geofenceName', geofencesOptions.find(x => x.value == data?.value)?.label ?? null);
                                                        onChange(data?.value ?? null);
                                                        onInputChange();

                                                    }}
                                                    value={geofencesOptions.find(x => x.value === value) ? { label: geofencesOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                    isDisabled={(selectedHasGeofence != undefined && !selectedHasGeofence)}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="geofenceId"

                                        defaultValue={getValues('geofenceId')} />
                                </FormItem>
                            </Col>}

                            {(hasReadCostCenterPolicy && !hideGeoFenceFilters) && <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>
                                        {t('charges.has_geofence')}
                                        <FaInfoCircle data-tip={t('charges.has_geofence_selected_tooltip')} data-for={'situationTooltip'} style={{ marginLeft: '0.5rem' }} />
                                    </Label>
                                    <div className={styles.situationDetails}>
                                        <div onClick={() => form.watch('geofenceId') ? undefined : onSelectHasGeofence(true)} style={{ width: '50%', cursor: form.watch('geofenceId') ? 'not-allowed' : 'pointer' }}>
                                            <div className={styles.labelContainer}>
                                                <div className={selectedHasGeofence || form.watch('geofenceId') ? styles.selected : styles.notSelected}>
                                                    <span>{t('common.yes')}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div onClick={() => form.watch('geofenceId') ? undefined : onSelectHasGeofence(false)} style={{ width: '50%', cursor: form.watch('geofenceId') ? 'not-allowed' : 'pointer' }}>
                                            <div className={styles.labelContainer}>
                                                <div className={(selectedHasGeofence != undefined && !selectedHasGeofence) ? styles.selected : styles.notSelected}>
                                                    <span>{t('common.no')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </FormItem>
                            </Col>}




                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.type')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            return (
                                                <Select
                                                    options={segmentsOptions}
                                                    isClearable
                                                    placeholder={t('vehicle.type')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        setValue('segmentName', segmentsOptions.find(x => x.value == data?.value)?.label ?? null);
                                                        onChange(data?.value ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={segmentsOptions.find(x => x.value === value) ? { label: segmentsOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="segmentId"
                                        defaultValue={getValues('segmentId')} />
                                </FormItem>
                            </Col>
                            <Col xs={6}>
                                <FormItem>
                                    <div className={styles.labelWithOperator}>
                                        <Label className={styles.bold}>{t('vehicle.local')}</Label>
                                        <div className={styles.operatorToggle}>
                                            <span title={t('common.filter_all_tooltip' as any) as string}>
                                                <Button
                                                    text={t('common.filter_all' as any) as string}
                                                    className={styles.firstChild}
                                                    preset={watch('localOperator') === 'AND' ? 'primary' : 'secondary'}
                                                    onClick={() => { setValue('localOperator', 'AND'); onInputChange(); }}
                                                    size={'small'}
                                                />
                                            </span>
                                            <span title={t('common.filter_any_tooltip' as any) as string}>
                                                <Button
                                                    text={t('common.filter_any' as any) as string}
                                                    className={styles.lastChild}
                                                    preset={watch('localOperator') !== 'AND' ? 'primary' : 'secondary'}
                                                    onClick={() => { setValue('localOperator', 'OR'); onInputChange(); }}
                                                    size={'small'}
                                                />
                                            </span>
                                        </div>
                                    </div>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            return (
                                                <Select
                                                    options={localsOptions}
                                                    isMulti
                                                    isClearable
                                                    placeholder={t('vehicle.local')}
                                                    onChange={(data: SelectValueLabel[]) => {
                                                        onChange(data?.map(d => d.value) ?? []);
                                                        setValue('localNames', data?.map(d => d.label) ?? []);
                                                        onInputChange();
                                                    }}
                                                    value={localsOptions.filter(x => (value || []).includes(x.value))}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="localIds"
                                        defaultValue={getValues('localIds') || []} />
                                </FormItem>
                            </Col>

                            {!isVehicle && <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('charges.vehicle_inactives')}</Label>
                                    <ToggleSwitch
                                        name="vehiclesInactive"
                                        form={form}
                                    />
                                </FormItem>
                            </Col>}

                        </Row>
                    </FormItem>
                </div>
                <div className={styles.buttonsFooter}>
                    <FormItem>
                        <Button
                            text={t('common.clear')}
                            size={'normal'}
                            preset={'secondary'}
                            onClick={clearFilters}
                            type='reset' />
                        <Button
                            type='submit'
                            text={t('common.apply')}
                            size={'normal'}
                        />
                    </FormItem>
                </div>
            </div>
        </form>
    );
}

export default memo(ChargeFiltersScreen);